import { useState, useEffect } from "react";
import { LinkFieldSearch } from "./LinkFieldSearch";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import type { FilterDescriptor } from "@/lib/erp";

type TranslationModule = Record<string, string>;

interface CreatedByFilterProps {
  value: FilterDescriptor[];
  onChange: (filters: FilterDescriptor[]) => void;
  translations?: TranslationModule;
}

export function CreatedByFilter({
  value,
  onChange,
  translations,
}: CreatedByFilterProps) {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  // Extract selected user from filters on mount/change
  useEffect(() => {
    const filter = value.find(
      (f) => f.field === "owner" && f._metadata?.section === "created_by"
    );
    if (filter && typeof filter.value === "string") {
      setSelectedUser(filter.value);
    } else {
      setSelectedUser(null);
    }
  }, [value]);

  const handleUserSelect = (userEmail: string | null) => {
    setSelectedUser(userEmail);
    updateFilter(userEmail);
  };

  const updateFilter = (userEmail: string | null) => {
    // Remove existing created_by filters
    const otherFilters = value.filter(
      (f) => !(f.field === "owner" && f._metadata?.section === "created_by")
    );

    // Add new filter if user is selected
    if (userEmail) {
      const newFilter: FilterDescriptor = {
        field: "owner",
        operator: "equals",
        value: userEmail,
        _metadata: {
          section: "created_by",
        },
      };
      onChange([...otherFilters, newFilter]);
    } else {
      onChange(otherFilters);
    }
  };

  const handleRemove = () => {
    setSelectedUser(null);
    updateFilter(null);
  };

  const t = translations || {};

  return (
    <div className="space-y-2">
        <LinkFieldSearch
          doctype="User"
          value={selectedUser}
          onChange={handleUserSelect}
          placeholder={t.selectUser || "Select user..."}
          fetchInitialItems={true}
          initialSearch="" // Changed from "%" to "" to avoid Frappe bug in some versions
          className="w-full"
        />

      {selectedUser && (
        <div className="flex items-center gap-2 mt-2">
          <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1">
            <span className="text-xs">{selectedUser}</span>
            <button
              onClick={handleRemove}
              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
              aria-label={t.removeUser || "Remove user"}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        </div>
      )}
    </div>
  );
}

